import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Check, ArrowRight, Sparkles, Send, Pencil, Plus, X, RotateCcw, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WEEK_ORDER = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];
const SHORT_LABEL = { mon: 'MÅN', tue: 'TIS', wed: 'ONS', thu: 'TOR', fri: 'FRE', sat: 'LÖR', sun: 'SÖN' };
const CAP_LABEL = { mon: 'Måndag', tue: 'Tisdag', wed: 'Onsdag', thu: 'Torsdag', fri: 'Fredag', sat: 'Lördag', sun: 'Söndag' };
const EMOJIS = ['🍳', '🛋️', '🛌', '🧥', '🛁', '🌟', '🌱', '🧹', '🧺', '🚪', '🪴', '🎨', '👶', '🐾', '💻', '🧴'];
const COLORS = ['#FFE5B4', '#FFC8DD', '#CDB4DB', '#BDE0FE', '#A2D2FF', '#FDF0D5', '#B9FBC0', '#FFAFCC'];

function TodayView({ today, onToggle, onPush, onEdit }) {
  if (!today) return null;
  return (
    <div className="space-y-4">
      {/* Hero — big Sweepy-style typography */}
      <div
        className="neo-card p-6 md:p-8"
        style={{ backgroundColor: `${today.color}55` }}
      >
        <div className="flex items-start gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold uppercase tracking-widest text-gray-600 mb-2">
              Idag är det {today.day_label}
            </div>
            <h1 className="text-3xl md:text-5xl font-['Fredoka'] font-bold leading-tight text-black">
              Dags för <span className="underline decoration-[6px] decoration-wavy" style={{ textDecorationColor: today.color }}>{today.room.toLowerCase()}</span> {today.emoji}
            </h1>
            <p className="mt-3 text-sm md:text-base text-gray-700 max-w-xl">
              Fem små steg. Du behöver inte göra allt — bara börja med ett. 💗
            </p>
          </div>
          <div
            className="hidden md:flex w-24 h-24 rounded-3xl border-2 border-black items-center justify-center text-6xl flex-shrink-0"
            style={{ backgroundColor: today.color }}
          >
            {today.emoji}
          </div>
        </div>

        {/* Progress bar */}
        <div className="mt-5">
          <div className="flex items-center justify-between text-sm font-bold mb-1">
            <span>{today.done_count} av {today.total_count} klara</span>
            <span>{today.progress_pct}%</span>
          </div>
          <div className="w-full h-4 rounded-full border-2 border-black bg-white overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{ width: `${today.progress_pct}%`, backgroundColor: today.color }}
            />
          </div>
        </div>
      </div>

      {/* Task list */}
      <div className="neo-card p-5 bg-white">
        <ol className="space-y-3">
          {today.tasks.map((t, idx) => (
            <li
              key={t.key}
              className="flex items-start gap-3"
              data-testid={`room-task-${t.key}`}
            >
              <button
                onClick={() => onToggle(today.day_key, t.key)}
                className={`w-8 h-8 rounded-lg border-2 border-black flex items-center justify-center transition-all flex-shrink-0 ${
                  t.done ? 'shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'bg-white hover:bg-gray-50'
                }`}
                style={t.done ? { backgroundColor: today.color } : {}}
                data-testid={`toggle-task-${t.key}`}
                aria-label={t.done ? 'Klar' : 'Bocka av'}
              >
                {t.done && <Check className="w-5 h-5 text-black" strokeWidth={3} />}
              </button>
              <div className="flex-1 pt-1">
                <div className="flex items-baseline gap-2">
                  <span className="font-['Fredoka'] font-bold text-gray-500 text-lg leading-none">{idx + 1}.</span>
                  <span className={`text-lg font-['Fredoka'] leading-snug ${t.done ? 'line-through text-gray-400' : 'text-gray-900'}`}>
                    {t.text}
                  </span>
                </div>
              </div>
            </li>
          ))}
        </ol>

        {today.total_count === 0 && (
          <div className="text-center py-6 text-gray-500">
            <p className="mb-2">Inga uppgifter för idag — vila 🌱</p>
            <button className="neo-btn px-4 py-2 bg-[#FFC8DD]" onClick={onEdit}>
              <Pencil className="w-4 h-4 inline mr-1" /> Lägg till uppgifter
            </button>
          </div>
        )}
      </div>

      {/* Action row */}
      <div className="grid grid-cols-2 gap-3">
        <button
          onClick={onPush}
          className="neo-btn py-3 bg-[#B9FBC0] flex items-center justify-center gap-2"
          data-testid="push-daily-flow-btn"
        >
          <Send className="w-4 h-4" />
          <span className="font-bold text-sm">Skicka till Idag-lista</span>
        </button>
        <button
          onClick={onEdit}
          className="neo-btn py-3 bg-white flex items-center justify-center gap-2"
          data-testid="edit-plan-btn"
        >
          <Pencil className="w-4 h-4" />
          <span className="font-bold text-sm">Anpassa veckan</span>
        </button>
      </div>

      {today.all_done && (
        <div className="neo-card p-4 bg-[#B9FBC0] flex items-center gap-3">
          <Sparkles className="w-5 h-5" />
          <div className="font-bold">
            Alla dagens städ-steg klara — du är fantastisk. Ditt husdjur mår extra bra idag 🐾💖
          </div>
        </div>
      )}
    </div>
  );
}

function WeekStrip({ week, onEdit }) {
  if (!week) return null;
  return (
    <div className="neo-card p-4 bg-white">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-['Fredoka'] font-bold">Veckans rotation</h3>
        <button
          onClick={onEdit}
          className="text-xs font-bold underline text-gray-600 hover:text-black"
        >
          Anpassa
        </button>
      </div>
      <div className="grid grid-cols-7 gap-2">
        {week.days.map(d => (
          <div
            key={d.day_key}
            className={`p-2 rounded-xl border-2 text-center transition-all ${
              d.is_today ? 'border-black shadow-[2px_2px_0px_0px_rgba(0,0,0,1)]' : 'border-gray-200'
            }`}
            style={{ backgroundColor: d.is_today ? `${d.color}` : `${d.color}30` }}
          >
            <div className="text-[10px] font-bold uppercase tracking-widest text-gray-700">
              {SHORT_LABEL[d.day_key]}
            </div>
            <div className="text-2xl mt-1">{d.emoji}</div>
            <div className="text-[10px] font-bold mt-1 line-clamp-2 leading-tight">
              {d.room}
            </div>
            <div className="text-[10px] text-gray-600 mt-1">
              {d.done_count}/{d.total_count}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function EditPlanModal({ plan, onClose, onSaved }) {
  const [days, setDays] = useState(() => {
    const clean = {};
    for (const k of WEEK_ORDER) {
      const d = plan?.days?.[k] || { room: '', emoji: '✨', color: '#FFC8DD', tasks: [] };
      clean[k] = { room: d.room || '', emoji: d.emoji || '✨', color: d.color || '#FFC8DD', tasks: [...(d.tasks || [])] };
    }
    return clean;
  });
  const [activeKey, setActiveKey] = useState('mon');
  const [saving, setSaving] = useState(false);

  const day = days[activeKey];

  const updateDay = (patch) => setDays({ ...days, [activeKey]: { ...day, ...patch } });
  const updateTask = (idx, val) => {
    const next = [...day.tasks];
    next[idx] = val;
    updateDay({ tasks: next });
  };
  const addTask = () => updateDay({ tasks: [...day.tasks, ''] });
  const removeTask = (idx) => updateDay({ tasks: day.tasks.filter((_, i) => i !== idx) });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { days: {} };
      for (const k of WEEK_ORDER) {
        const d = days[k];
        payload.days[k] = {
          room: (d.room || '').trim() || 'Rum',
          emoji: d.emoji || '✨',
          color: d.color || '#FFC8DD',
          tasks: (d.tasks || []).map(t => t.trim()).filter(Boolean),
        };
      }
      await axios.put(`${API}/room-rotation/plan`, payload, { withCredentials: true });
      toast.success('Sparat 💾');
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Kunde inte spara');
    } finally {
      setSaving(false);
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm('Återställ hela veckan till Sofias standardrotation?')) return;
    try {
      await axios.post(`${API}/room-rotation/reset-defaults`, {}, { withCredentials: true });
      toast.success('Återställt 🌸');
      onSaved();
      onClose();
    } catch (e) {
      toast.error('Kunde inte återställa');
    }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="neo-card bg-white w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b-2 border-black flex items-center justify-between">
          <h2 className="text-xl font-['Fredoka'] font-bold">Anpassa städ-veckan</h2>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded" data-testid="edit-close-btn">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Day tabs */}
        <div className="flex gap-1 p-3 border-b-2 border-black overflow-x-auto">
          {WEEK_ORDER.map(k => (
            <button
              key={k}
              onClick={() => setActiveKey(k)}
              className={`px-3 py-1.5 rounded-lg border-2 text-xs font-bold whitespace-nowrap transition-all ${
                activeKey === k ? 'border-black bg-yellow-100' : 'border-gray-200 bg-white'
              }`}
            >
              {SHORT_LABEL[k]}
            </button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto space-y-4 flex-1">
          <div className="text-sm font-bold text-gray-600">{CAP_LABEL[activeKey]}</div>

          <div>
            <label className="text-sm font-bold mb-1 block">Rum</label>
            <input
              type="text"
              value={day.room}
              onChange={(e) => updateDay({ room: e.target.value })}
              className="neo-input w-full"
              placeholder="t.ex. Badrummet"
              data-testid="edit-room-input"
            />
          </div>

          <div>
            <label className="text-sm font-bold mb-1 block">Emoji</label>
            <div className="flex flex-wrap gap-1">
              {EMOJIS.map(e => (
                <button
                  key={e}
                  onClick={() => updateDay({ emoji: e })}
                  className={`w-9 h-9 rounded-lg border-2 text-xl transition-all ${
                    day.emoji === e ? 'border-black bg-yellow-100 scale-110' : 'border-gray-200 bg-white'
                  }`}
                >
                  {e}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="text-sm font-bold mb-1 block">Färg</label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map(c => (
                <button
                  key={c}
                  onClick={() => updateDay({ color: c })}
                  className={`w-8 h-8 rounded-full border-2 transition-all ${
                    day.color === c ? 'border-black scale-125' : 'border-gray-300'
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <label className="text-sm font-bold">Mikro-uppgifter</label>
              <button
                onClick={addTask}
                className="text-xs font-bold underline text-gray-600 hover:text-black flex items-center gap-1"
              >
                <Plus className="w-3 h-3" /> Lägg till
              </button>
            </div>
            <div className="space-y-2">
              {day.tasks.map((t, idx) => (
                <div key={idx} className="flex items-center gap-2">
                  <span className="text-gray-400 font-bold w-5 text-right">{idx + 1}.</span>
                  <input
                    type="text"
                    value={t}
                    onChange={(e) => updateTask(idx, e.target.value)}
                    className="neo-input flex-1"
                    placeholder="t.ex. Torka spegeln"
                  />
                  <button
                    onClick={() => removeTask(idx)}
                    className="p-2 hover:bg-red-100 rounded"
                    aria-label="Ta bort"
                  >
                    <Trash2 className="w-4 h-4 text-red-400" />
                  </button>
                </div>
              ))}
              {day.tasks.length === 0 && (
                <p className="text-xs text-gray-500">Inga uppgifter än — klicka "Lägg till" ovan.</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-4 border-t-2 border-black flex gap-2">
          <button
            onClick={resetDefaults}
            className="neo-btn px-3 py-2 bg-gray-100 flex items-center gap-1 text-sm"
            data-testid="reset-defaults-btn"
          >
            <RotateCcw className="w-3 h-3" /> Återställ
          </button>
          <div className="flex-1" />
          <button
            onClick={onClose}
            className="neo-btn px-4 py-2 bg-gray-200 text-sm"
          >
            Avbryt
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="neo-btn px-4 py-2 bg-[#B9FBC0] disabled:opacity-50 text-sm font-bold"
            data-testid="save-plan-btn"
          >
            {saving ? 'Sparar...' : 'Spara 💾'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function RoomRotation() {
  const [today, setToday] = useState(null);
  const [week, setWeek] = useState(null);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);

  const fetchAll = useCallback(async () => {
    try {
      const [t, w, p] = await Promise.all([
        axios.get(`${API}/room-rotation/today`, { withCredentials: true }),
        axios.get(`${API}/room-rotation/week`, { withCredentials: true }),
        axios.get(`${API}/room-rotation/plan`, { withCredentials: true }),
      ]);
      setToday(t.data);
      setWeek(w.data);
      setPlan(p.data);
    } catch (e) {
      console.error(e);
      toast.error('Kunde inte hämta städ-rotationen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleTask = async (dayKey, taskKey) => {
    try {
      await axios.post(
        `${API}/room-rotation/task/toggle`,
        { day_key: dayKey, task_key: taskKey },
        { withCredentials: true }
      );
      fetchAll();
    } catch (e) {
      toast.error('Kunde inte bocka av');
    }
  };

  const pushToFlow = async () => {
    try {
      const { data } = await axios.post(
        `${API}/room-rotation/push-to-daily-flow`,
        {},
        { withCredentials: true }
      );
      if (data.created + data.updated === 0) {
        toast('Allt är redan avklarat idag 🌟');
      } else {
        toast.success(`${data.created} tasks skickade till Idag-listan ✨`);
      }
    } catch (e) {
      toast.error('Kunde inte skicka');
    }
  };

  if (loading) {
    return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar din städ-rutan... 🧹</div>;
  }

  return (
    <div className="space-y-4 max-w-3xl mx-auto" data-testid="room-rotation">
      <TodayView
        today={today}
        onToggle={toggleTask}
        onPush={pushToFlow}
        onEdit={() => setEditing(true)}
      />
      <WeekStrip week={week} onEdit={() => setEditing(true)} />

      {editing && (
        <EditPlanModal
          plan={plan}
          onClose={() => setEditing(false)}
          onSaved={fetchAll}
        />
      )}
    </div>
  );
}
